import { useState } from "react";
import { Link } from "react-router-dom";
import { PageLoader } from "#/components/common/PageLoader";
import { ErrorState } from "#/components/common/ErrorState";
import { EmptyState } from "#/components/common/EmptyState";
import { DataPagination } from "#/components/data-table/DataPagination";
import { useAllTrainingRequests } from "../hooks";

function fmt(v?: string | null) {
  if (!v) return "—";
  try { return new Date(v).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); }
  catch { return v; }
}

function fmtDateTime(v?: string | null) {
  if (!v) return "—";
  try { return new Date(v).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return v; }
}

function diffDays(start?: string | null, end?: string | null) {
  if (!start || !end) return null;
  try {
    const diff = new Date(end).getTime() - new Date(start).getTime();
    const days = Math.round(diff / 86_400_000) + 1;
    return days > 0 ? days : null;
  } catch { return null; }
}

export default function TrainingSessionsPage() {
  const [page, setPage] = useState(0);

  // Sessions = approved/completed requests that have a start date (they represent scheduled training)
  const query = useAllTrainingRequests(page, 15, "APPROVED");

  if (query.isLoading) return <PageLoader label="Loading sessions…" />;
  if (query.isError) return <ErrorState title="Failed to load" message={String(query.error)} />;

  const data = query.data!;
  const items = data.content.filter((r) => r.trainingStartDate);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Training Sessions</h1>
      </div>

      {items.length === 0 ? (
        <EmptyState
          title="No scheduled sessions"
          description="Approved training requests with a start date will appear here."
        />
      ) : (
        <>
          <div className="rounded-2xl border bg-white shadow-sm overflow-hidden divide-y">
            {items.map((r) => {
              const days = diffDays(r.trainingStartDate, r.trainingEndDate);
              return (
                <Link
                  key={r.id}
                  to={`/hr/training/${r.id}`}
                  className="flex flex-wrap items-center gap-4 px-5 py-4 hover:bg-slate-50 transition-colors"
                >
                  {/* Date badge */}
                  <div className="flex h-12 w-12 shrink-0 flex-col items-center justify-center rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700">
                    <span className="text-xs font-semibold leading-none">
                      {new Date(r.trainingStartDate + "T00:00:00").toLocaleString("default", { month: "short" }).toUpperCase()}
                    </span>
                    <span className="text-lg font-black leading-tight">
                      {new Date(r.trainingStartDate + "T00:00:00").getDate()}
                    </span>
                  </div>

                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-slate-800 text-sm truncate">{r.trainingTitle}</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {r.employeeName}
                      {r.providerName && ` · ${r.providerName}`}
                      {r.venue && ` · ${r.venue}`}
                    </p>
                  </div>

                  <div className="text-right text-xs text-slate-500 shrink-0">
                    <p>{fmt(r.trainingStartDate)}{r.trainingEndDate && r.trainingEndDate !== r.trainingStartDate ? ` → ${fmt(r.trainingEndDate)}` : ""}</p>
                    {days && <p className="text-slate-400">{days} day{days !== 1 ? "s" : ""}</p>}
                  </div>
                </Link>
              );
            })}
          </div>
          <DataPagination
            page={data.number}
            totalPages={data.totalPages}
            totalElements={data.totalElements}
            size={data.size}
            onPageChange={setPage}
            onSizeChange={() => setPage(0)}
          />
        </>
      )}
    </div>
  );
}
